<template>
  <div class="navigation-tiers">
    <div class="navigation-tiers__wrapper grid container-no-padding">
      <ul
        class="navigation-tiers__listing grid col"
        :class="multiRowBanner ? 'l1-6' : 'l1-9'"
      >
        <!-- Links without children (Tier 2) -->
        <div class="col l2">
          <template
            v-for="(link, index) of menuItemsWithoutChildren"
            :key="index"
          >
            <li
              v-if="isValidLink(link)"
              class="navigation-tiers__tier"
            >
              <a
                :href="link.url"
                class="navigation-tiers__link text-heading-xs"
                :style="{
                  color: getLinkSpecialColor(link),
                }"
                v-text="link.title"
              />
            </li>
          </template>
        </div>

        <template
          v-for="(link, index) of menuItemsWithChildren"
          :key="index"
        >
          <div
            :class="getNavigationTierClass(link)"
            class="col l2"
          >
            <template
              v-for="(child, childIndex) of link.links"
              :key="childIndex"
            >
              <li
                v-if="isValidHeading(child.title)"
                class="navigation-tiers__tier2 navigation-tiers__tier2--heading"
              >
                <a
                  :href="child.url"
                  class="navigation-tiers__link text-heading-2xs"
                  v-text="getHeadingText(child.title)"
                />
              </li>

              <li
                v-if="isValidLink(child)"
                class="navigation-tiers__tier2"
              >
                <a
                  :href="child.url"
                  class="navigation-tiers__link text-body-s"
                  v-text="child.title"
                />
              </li>
            </template>
          </div>
        </template>
      </ul>

      <!-- Banners -->
      <div
        v-if="currentItemBanners.length"
        :class="multiRowBanner ? 'col l7-12' : 'col l10-12'"
      >
        <div
          class="navigation-tiers__banner-wrapper"
          :class="{ 'navigation-tiers__banner-wrapper--multiple': multiRowBanner }"
        >
          <div
            v-for="(banner, index) of currentItemBanners"
            :key="banner.title"
            class="navigation-tiers__banner col"
            :class="index % 2 === 0 ? 'l4-7' : 'l1-3'"
          >
            <a
              class="text-heading-2xs"
              :href="banner.url"
            >
              <responsive-image
                class="navigation-tiers__banner-image"
                block-class="navigation-tiers"
                :image="{
                  alt: banner.title,
                  src: getBannerSrc(banner.title),
                }"
                ratio="3:2"
                sizes="320px"
              />

              <p
                class="navigation-tiers__banner-title text-heading-2xs"
                v-text="getBannerTitle(banner)"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: navigation-tiers (navigation-tiers)
 * -----------------------------------------------------------------------------
 * Handles the tier structures for the desktop handles.
 *
 */
import ResponsiveImage from '~global/images/responsive-image'

import { specialLinks } from '~/config/navigations'
import { validateNavigationMenuTitle } from '~/helpers/vue'

export default {
  name: 'NavigationTiers',

  components: {
    ResponsiveImage,
  },

  props: {
    tier: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {

    }
  },

  computed: {

    /**
     * Get current item banners.
     * @returns {Array}
     */
    currentItemBanners() {
      return this.tier?.links?.filter((item) => {
        return item.title.includes(specialLinks.banner)
      }) || []
    },

    /**
     * Get menu items without children.
     * @returns {Array}
     */
    menuItemsWithoutChildren() {
      return this.tier.links.filter((item) => {
        return (
          !item.links.length ||
          item.links[0]?.title.includes(specialLinks.color)
        )
      })
    },

    /**
     * Get menu items with children.
     * @returns {Array}
     */
    menuItemsWithChildren() {
      return this.tier.links.filter((item) => {
        return (
          item.title.includes(specialLinks.col) &&
          !item.title.includes(specialLinks.colBreak)
        )
      })
    },

    /**
     * Checks if multiple row needed in the grid layout.
     * @returns {boolean}
     */
    multiRowBanner() {
      return this.currentItemBanners.length > 2
    },
  },

  methods: {

    /**
     * Check if link is valid not a customiser.
     * @param {string} title
     * @return {boolean}
     */
    isValidLink({ title }) {
      return !validateNavigationMenuTitle(title)
    },

    /**
     * Check if link is valid heading.
     * @param {String} title - Title.
     * @returns {String}
     */
    isValidHeading(title) {
      return title.includes(specialLinks.heading)
    },

    /**
     * Get the banner display title.
     * - Uses [banner-label] child link if available (localisation).
     * - Falls back to collection title derived from the url.
     * @param {Object} banner - Banner link object.
     * @returns {string}
     */
    getBannerTitle(banner) {
      const labelLink = banner.links?.find(
        (link) => link.title.includes(specialLinks.bannerLabel),
      )

      if (labelLink) {
        return labelLink.title.replace(specialLinks.bannerLabel, '')
      }

      return banner.url.split('/').pop().replace(/-/g, ' ')
    },

    /**
     * Check if the tier has a color inside its links.
     * @param {object} link
     * @return {string}
     */
    getLinkSpecialColor(link) {
      return link.links?.find((item) => {
        return item.title.includes(specialLinks.color)
      })?.title?.replace(specialLinks.color, '')
    },

    /**
     * Get navigation tier class.
     * @param {Object} link - Navigation link.
     * @returns {String}
     */
    getNavigationTierClass(link) {
      return `navigation-tiers__${link.title.replace('[', '').replace(']', '')}`
    },

    /**
     * Get heading text.
     * @param {String} title - Title.
     * @returns {String}
     */
    getHeadingText(title) {
      return title.replace(specialLinks.heading, '')
    },

    /**
     * Get banner image src.
     * @param {String} title - Title.
     * @returns {String}
     */
    getBannerSrc(title) {
      return title.replace(specialLinks.banner, '')
    },
  },
}
</script>

<style lang="scss">
@import './navigation-tiers';
</style>
